import React from 'react';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';

interface UseCase {
  emoji: string;
  title: string;
  description: string;
}

const UseCaseCard: React.FC<{ useCase: UseCase; index: number }> = ({ useCase, index }) => {
  const [ref, isVisible] = useIntersectionObserver({ threshold: 0.1 });

  return (
    <div
      ref={ref}
      className={`bg-white p-8 rounded-xl text-center transition-all duration-500 hover:scale-105 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="text-5xl mb-4">{useCase.emoji}</div>
      <h4 className="text-xl font-bold text-soez-blue mb-3">{useCase.title}</h4>
      <p className="text-gray-600 text-sm">{useCase.description}</p>
    </div>
  );
};

const UseCases: React.FC = () => {
  const useCases: UseCase[] = [
    {
      emoji: '🏢',
      title: 'Corporate Enterprises',
      description:
        'Secure internal communications with full admin control, data ownership, and instant access revocation.',
    },
    {
      emoji: '🏦',
      title: 'Financial Institutions',
      description:
        'Bank-grade security, compliance-ready data management, and encrypted communications for sensitive operations.',
    },
    {
      emoji: '🏫',
      title: 'Educational Institutions',
      description:
        'Safe, private communication for students, teachers, and parents with hidden contact details and controlled groups.',
    },
    {
      emoji: '🛒',
      title: 'Retail & Logistics',
      description:
        'QR-based attendance, location check-ins, and instant communication for distributed teams and shift workers.',
    },
    {
      emoji: '🌐',
      title: 'Global Organizations',
      description:
        'Real-time translation, multilingual support, and seamless collaboration across time zones and languages.',
    },
    {
      emoji: '🏥',
      title: 'Healthcare & Regulated Industries',
      description:
        'GDPR-compliant data handling, complete audit trails, and privacy-first design for sensitive environments.',
    },
  ];

  return (
    <section id="use-cases" className="py-20 px-8 bg-gray-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-soez-blue mb-4">Built for Your Industry</h2>
          <div className="w-20 h-1 bg-soez-orange mx-auto mb-6" />
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            From corporates to schools, SoEz adapts to your unique communication needs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {useCases.map((useCase, index) => (
            <UseCaseCard key={index} useCase={useCase} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default UseCases;
